"use client";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import Image from "next/image";
import { FaTimes } from "react-icons/fa";

interface PackageLoginModalProps {
  isOpen: boolean;
  closeModal: () => void;
  /** Where to land after sign-in completes. Defaults to current page. */
  callbackUrl?: string;
}

/**
 * "Sign in to continue" modal shown when a logged-out student tries to
 * buy a package. PR 3 (#619): kicks off the backend Google OAuth
 * handshake — no more credentials form.
 */
const PackageLoginModal: React.FC<PackageLoginModalProps> = ({
  isOpen,
  closeModal,
  callbackUrl,
}) => {
  const handleGoogleSignIn = () => {
    const target =
      callbackUrl ?? (typeof window !== "undefined" ? window.location.href : "/");
    const params = new URLSearchParams({ callbackUrl: target });
    window.location.assign(`/api/auth/oauth/google/start?${params.toString()}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
      <DialogContent
        className="max-w-md overflow-hidden rounded-3xl bg-white p-0 [&>button:last-child]:hidden"
        data-testid="packageLoginModal"
      >
        <div className="relative flex flex-col items-center justify-center rounded-t-3xl bg-[#27335D] px-6 pt-4 pb-1 text-white">
          <button
            onClick={closeModal}
            data-testid="packageLoginModalCloseButton"
            className="absolute top-4 right-4 text-xl text-white transition hover:text-gray-300 sm:text-2xl"
          >
            <FaTimes />
          </button>
          <Image
            src="/images/RawiahLogos/headerlogo.svg"
            alt="Rawiah"
            width={100}
            height={100}
            unoptimized
            priority
            loading="eager"
            className="mb-5 w-24"
          />
          <p className="text-center font-semibold text-white">
            You&apos;re just one step away from unlocking your package
          </p>
        </div>

        <div className="rounded-b-3xl border-8 border-[#27335D] bg-white px-12 py-8">
          <h2 className="mb-5 text-center text-2xl font-bold text-[#27335D]">
            Sign in to continue
          </h2>
          <button
            type="button"
            onClick={handleGoogleSignIn}
            data-testid="packageLoginGoogleButton"
            className="flex w-full items-center justify-center gap-3 rounded-xl border-2 border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md"
          >
            <Image
              src="/images/google.png"
              alt=""
              width={20}
              height={20}
              className="h-5 w-5"
            />
            Continue with Google
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackageLoginModal;
